import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Settings, Plus, X, Save, Eye, EyeOff, Lock, Unlock, CheckSquare, Square } from 'lucide-react';
import LearningComponentsService from '../../services/learningComponentsService';
import LearningComponentPicker from './LearningComponentPicker';
import { initializeLearningComponents, checkLearningComponentsInitialized } from '../../services/initializeLearningComponents';
import './SessionComponentConfig.css';

const SessionComponentConfig = ({ sessionId, courseId, onSave, onCancel }) => {
  const [courseComponents, setCourseComponents] = useState([]);
  const [sessionComponents, setSessionComponents] = useState([]);
  const [initialSessionComponentIds, setInitialSessionComponentIds] = useState([]);
  const [initialSessionSnapshot, setInitialSessionSnapshot] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [bulkSelection, setBulkSelection] = useState([]);
  const [showBulkMode, setShowBulkMode] = useState(false);

  const loadComponentConfigurations = useCallback(async () => {
    try {
      setLoading(true);

      const initialized = await checkLearningComponentsInitialized();
      if (!initialized) {
        setIsInitializing(true);
        await initializeLearningComponents();
      }

      const [sessionData, courseData] = await Promise.all([
        sessionId ? LearningComponentsService.getSessionComponents(sessionId) : [],
        courseId ? LearningComponentsService.getCourseComponents(courseId) : []
      ]);

      const normalizedSession = sessionData.map((comp) => ({
        ...comp,
        inherited: false,
        config: comp.config || {}
      }));

      const normalizedCourse = courseData.map((comp) => ({
        ...comp,
        inherited: true,
        config: comp.config || {}
      }));

      setSessionComponents(normalizedSession);
      setInitialSessionComponentIds(normalizedSession.map((comp) => comp.id));
      setInitialSessionSnapshot(
        normalizedSession.map((comp) => ({ id: comp.id, configHash: JSON.stringify(comp.config || {}) }))
      );
      setCourseComponents(normalizedCourse);
      setBulkSelection([]);
      setShowBulkMode(false);
    } catch (error) {
      console.error('Error loading component configurations:', error);
    } finally {
      setLoading(false);
      setIsInitializing(false);
    }
  }, [sessionId, courseId]);

  useEffect(() => {
    loadComponentConfigurations();
  }, [loadComponentConfigurations]);

  const handlePickerSelectionChange = useCallback(() => {}, []);

  const handleConfirmSelection = (selectedComponents = []) => {
    if (selectedComponents.length === 0) {
      setShowPicker(false);
      return;
    }

    setSessionComponents((prev) => {
      const existingIds = new Set(prev.map((comp) => comp.id));
      const additions = selectedComponents
        .filter((comp) => !existingIds.has(comp.id))
        .map((comp) => ({
          ...comp,
          inherited: false,
          config: comp.config || {}
        }));

      return [...prev, ...additions];
    });

    setShowPicker(false);
  };

  const handleComponentConfigChange = (componentId, config) => {
    setSessionComponents((prev) =>
      prev.map((comp) =>
        comp.id === componentId ? { ...comp, config: { ...comp.config, ...config } } : comp
      )
    );
  };

  const handleRemoveComponent = (componentId) => {
    setSessionComponents((prev) => prev.filter((comp) => comp.id !== componentId));
    setBulkSelection((prev) => prev.filter((id) => id !== componentId));
  };

  const handleBulkSelect = (componentId) => {
    setBulkSelection((prev) =>
      prev.includes(componentId)
        ? prev.filter((id) => id !== componentId)
        : [...prev, componentId]
    );
  };

  const handleSelectAll = () => {
    if (bulkSelection.length === sessionComponents.length) {
      setBulkSelection([]);
    } else {
      setBulkSelection(sessionComponents.map((comp) => comp.id));
    }
  };

  const handleBulkRemove = () => {
    if (bulkSelection.length === 0) {
      return;
    }

    setSessionComponents((prev) => prev.filter((comp) => !bulkSelection.includes(comp.id)));
    setBulkSelection([]);
  };

  const handleSaveConfiguration = async () => {
    if (!sessionId) {
      alert('Session ID is required to save configuration.');
      return;
    }

    try {
      setSaving(true);

      const currentIds = sessionComponents.map((comp) => comp.id);
      const addedIds = currentIds.filter((id) => !initialSessionComponentIds.includes(id));
      const removedIds = initialSessionComponentIds.filter((id) => !currentIds.includes(id));

      if (addedIds.length > 0) {
        await LearningComponentsService.addComponentsToSession(sessionId, addedIds, 'admin');
      }

      for (const componentId of removedIds) {
        await LearningComponentsService.removeComponentFromSession(sessionId, componentId);
      }

      for (const component of sessionComponents) {
        if (component.config && Object.keys(component.config).length > 0) {
          await LearningComponentsService.updateSessionComponentConfig(
            sessionId,
            component.id,
            component.config
          );
        }
      }

      await loadComponentConfigurations();
      onSave && onSave();
    } catch (error) {
      console.error('Error saving configuration:', error);
      alert('Error saving configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const effectiveComponents = useMemo(() => {
    const componentMap = new Map();
    courseComponents.forEach((comp) => componentMap.set(comp.id, comp));
    sessionComponents.forEach((comp) => componentMap.set(comp.id, comp));
    return Array.from(componentMap.values());
  }, [courseComponents, sessionComponents]);

  const hasConfigurationChanges = useMemo(() => {
    if (initialSessionSnapshot.length !== sessionComponents.length) {
      return true;
    }

    const snapshotMap = new Map(initialSessionSnapshot.map((entry) => [entry.id, entry.configHash]));
    return sessionComponents.some((comp) => {
      const snapshotHash = snapshotMap.get(comp.id);
      const currentHash = JSON.stringify(comp.config || {});
      return snapshotHash !== currentHash;
    });
  }, [initialSessionSnapshot, sessionComponents]);

  const summaryStats = useMemo(
    () => ({
      sessionOverrides: sessionComponents.length,
      inheritedComponents: courseComponents.length,
      totalComponents: effectiveComponents.length
    }),
    [sessionComponents, courseComponents, effectiveComponents]
  );

  const guidedSteps = useMemo(() => {
    const hasSessionOverrides = sessionComponents.length > 0;
    const step1Status = courseComponents.length > 0 ? 'completed' : 'current';
    const step2Status = hasSessionOverrides ? (hasConfigurationChanges ? 'current' : 'completed') : 'current';
    const step3Status = hasSessionOverrides
      ? hasConfigurationChanges
        ? 'current'
        : 'completed'
      : 'upcoming';

    return [
      {
        id: 1,
        title: 'Review course defaults',
        description: 'Confirm what the course already provides.',
        status: step1Status
      },
      {
        id: 2,
        title: 'Customize session overrides',
        description: 'Add or adjust components specific to this session.',
        status: step2Status
      },
      {
        id: 3,
        title: 'Finalize & save',
        description: 'Lock in changes so students see the right experience.',
        status: step3Status
      }
    ];
  }, [courseComponents.length, sessionComponents.length, hasConfigurationChanges]);

  if (loading || isInitializing) {
    return (
      <div className="session-config-loading">
        <div className="loading-spinner"></div>
        <p>
          {isInitializing
            ? 'Initializing learning components database...'
            : 'Loading component configuration...'}
        </p>
      </div>
    );
  }

  return (
    <div className="session-component-config">
      <div className="config-header">
        <h3>
          <Settings size={24} />
          Session Component Configuration
        </h3>
        <p>
          Configure interactive components for this session. Session-level settings override
          course-level configurations.
        </p>
      </div>

      <div className="config-body">
        <div className="config-summary">
          <div className="summary-card">
            <span className="summary-label">Session overrides</span>
            <strong className="summary-value">{summaryStats.sessionOverrides}</strong>
            <span className="summary-hint">Custom to this session</span>
          </div>
          <div className="summary-card">
            <span className="summary-label">Inherited from course</span>
            <strong className="summary-value">{summaryStats.inheritedComponents}</strong>
            <span className="summary-hint">Auto-applied defaults</span>
          </div>
          <div className="summary-card highlight">
            <span className="summary-label">Total active components</span>
            <strong className="summary-value">{summaryStats.totalComponents}</strong>
            <span className="summary-hint">What students will see</span>
          </div>
        </div>

        <div className="guided-flow-panel">
          <div className="guided-flow-header">
            <div>
              <span className="guided-label">Guided setup</span>
              <p>Follow the steps below to keep the flow intentional.</p>
            </div>
            <span
              className={`change-pill ${hasConfigurationChanges ? 'pending' : 'saved'}`}
            >
              {hasConfigurationChanges ? 'Unsaved adjustments' : 'All changes saved'}
            </span>
          </div>
          <div className="guided-flow-steps">
            {guidedSteps.map((step) => (
              <div key={step.id} className={`flow-step ${step.status}`}>
                <div className="step-index">{step.id}</div>
                <div>
                  <p className="step-title">{step.title}</p>
                  <p className="step-description">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {showPicker && (
          <div className="picker-modal">
            <div className="modal-content">
              <div className="modal-header">
                <h4>Add Components to Session</h4>
                <button className="close-btn" onClick={() => setShowPicker(false)}>
                  <X size={20} />
                </button>
              </div>
              <div className="modal-body">
                <LearningComponentPicker
                  onComponentsSelected={handlePickerSelectionChange}
                  onConfirmSelection={handleConfirmSelection}
                  selectedComponents={sessionComponents}
                  maxComponents={10}
                />
              </div>
            </div>
          </div>
        )}

        <div className="config-sections">
          <div className="config-section">
            <div className="step-label">
              <span>Step 1</span>
              <strong>Review course defaults</strong>
            </div>
            <div className="section-header">
              <h4>
                <Lock size={18} />
                Course Components (Inherited)
              </h4>
              <span className="inheritance-badge">Can be overridden at session level</span>
            </div>

            {courseComponents.length === 0 ? (
              <div className="empty-section subtle">
                <p>No course-level defaults are active.</p>
                <p className="hint">You can build this session from scratch.</p>
              </div>
            ) : (
              <div className="components-list">
                {courseComponents.map((component) => (
                  <ComponentConfigCard
                    key={`course-${component.id}`}
                    component={component}
                    onConfigChange={() => {}}
                    onRemove={() => {}}
                    showBulkMode={false}
                    isSelected={false}
                    onBulkSelect={() => {}}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="config-section">
            <div className="step-label">
              <span>Step 2</span>
              <strong>Customize session overrides</strong>
            </div>
            <div className="section-header">
              <h4>
                <Unlock size={18} />
                Session Components (Overrides)
              </h4>
              <div className="header-actions">
                {sessionComponents.length > 0 && (
                  <button className="bulk-mode-btn" onClick={() => setShowBulkMode(!showBulkMode)}>
                    {showBulkMode ? <X size={16} /> : <CheckSquare size={16} />}
                    {showBulkMode ? 'Exit Bulk Mode' : 'Bulk Select'}
                  </button>
                )}
                <button className="add-btn" onClick={() => setShowPicker(true)}>
                  <Plus size={16} />
                  Add Components
                </button>
              </div>
            </div>

            {sessionComponents.length === 0 ? (
              <div className="empty-section">
                <p>No session-specific components configured.</p>
                <p className="hint">Start here once you finish reviewing the course defaults.</p>
              </div>
            ) : (
              <>
                {showBulkMode && (
                  <div className="bulk-actions">
                    <div className="bulk-info">
                      <button className="select-all-btn" onClick={handleSelectAll}>
                        {bulkSelection.length === sessionComponents.length ? (
                          <Square size={16} />
                        ) : (
                          <CheckSquare size={16} />
                        )}
                        {bulkSelection.length === sessionComponents.length
                          ? 'Deselect All'
                          : 'Select All'}
                      </button>
                      <span className="selection-count">
                        {bulkSelection.length} of {sessionComponents.length} selected
                      </span>
                    </div>
                    {bulkSelection.length > 0 && (
                      <button className="bulk-remove-btn" onClick={handleBulkRemove} disabled={saving}>
                        <X size={16} />
                        Remove Selected ({bulkSelection.length})
                      </button>
                    )}
                  </div>
                )}

                <div className="components-list">
                  {sessionComponents.map((component) => (
                    <ComponentConfigCard
                      key={`session-${component.id}`}
                      component={component}
                      onConfigChange={(config) => handleComponentConfigChange(component.id, config)}
                      onRemove={() => handleRemoveComponent(component.id)}
                      showBulkMode={showBulkMode}
                      isSelected={bulkSelection.includes(component.id)}
                      onBulkSelect={() => handleBulkSelect(component.id)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="config-actions">
          <div className={`change-indicator ${hasConfigurationChanges ? 'pending' : 'saved'}`}>
            {hasConfigurationChanges ? 'Finish Step 3: Save your changes' : 'Step 3 complete'}
          </div>
          <button className="cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          <button className="save-btn" onClick={handleSaveConfiguration} disabled={saving}>
            {saving ? (
              <>
                <span className="btn-spinner" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              <>
                <Save size={18} />
                Save Configuration
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const ComponentConfigCard = ({
  component,
  onConfigChange,
  onRemove,
  showBulkMode,
  isSelected,
  onBulkSelect
}) => {
  const [localConfig, setLocalConfig] = useState(component.config || {});
  const [showConfig, setShowConfig] = useState(false);

  useEffect(() => {
    setLocalConfig(component.config || {});
  }, [component]);

  const handleConfigChange = (key, value) => {
    const updatedConfig = { ...localConfig, [key]: value };
    setLocalConfig(updatedConfig);
    onConfigChange(updatedConfig);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner':
        return '#4CAF50';
      case 'intermediate':
        return '#FF9800';
      case 'advanced':
        return '#F44336';
      default:
        return '#9E9E9E';
    }
  };

  return (
    <div
      className={`component-config-card ${component.inherited ? 'inherited' : ''} ${
        isSelected ? 'bulk-selected' : ''
      }`}
    >
      <div className="card-header">
        {showBulkMode && !component.inherited && (
          <div className={`component-card-checkbox ${isSelected ? 'checked' : ''}`} onClick={onBulkSelect}>
            {isSelected && <span>✓</span>}
          </div>
        )}

        <div className="component-info">
          <h5>{component.name}</h5>
          <div className="component-meta">
            <span className="difficulty-badge" style={{ backgroundColor: getDifficultyColor(component.difficulty) }}>
              {component.difficulty || 'unknown'}
            </span>
            <span className="source-badge">{component.inherited ? 'Course' : 'Session'}</span>
          </div>
        </div>

        <div className="card-actions">
          <button
            className="action-btn"
            onClick={() => setShowConfig(!showConfig)}
            title="Toggle configuration"
          >
            {showConfig ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>

          {!component.inherited && (
            <button className="action-btn danger" onClick={onRemove} title="Remove component">
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {component.description && <p className="component-description">{component.description}</p>}

      {showConfig && (
        <div className="component-configuration">
          <h6>Configuration</h6>
          <div className="config-fields">
            {Object.keys(localConfig).length === 0 && (
              <p className="config-hint">No configurable options for this component.</p>
            )}
            {Object.entries(localConfig).map(([key, value]) => (
              <div key={key} className="config-field">
                <label>{key}</label>
                {typeof value === 'boolean' ? (
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => handleConfigChange(key, e.target.checked)}
                    disabled={component.inherited}
                  />
                ) : typeof value === 'string' || typeof value === 'number' ? (
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => handleConfigChange(key, e.target.value)}
                    disabled={component.inherited}
                  />
                ) : (
                  <input
                    type="text"
                    value={JSON.stringify(value)}
                    onChange={(e) => {
                      try {
                        handleConfigChange(key, JSON.parse(e.target.value));
                      } catch (err) {
                        // Invalid JSON, ignore
                      }
                    }}
                    disabled={component.inherited}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionComponentConfig;
